import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  EVAL_STATUS,
  EVAL_STATUS_NOTIFIED,
  POINTS_ALL,
} from "../../../../constants/index";
import {
  ScoreCard,
  ScoreValue,
  ScoreLabel,
  InfoCard,
  UserAvatar,
  UserInfo,
  UserTitle,
  UserName,
  YearBadge,
  SectionTitle,
  ObjectiveCard,
  ObjectiveHeader,
  ObjectiveNumber,
  ObjectiveWeight,
  CompletionIndicator,
  ObjectiveTitle,
  ObjectiveDescription,
  CompetencyCard,
  CompetencyHeader,
  CompetencyBody,
  CommentBox,
  CommentLabel,
  CommentText,
  ActionButton,
  StatusBadge,
  CollapseIcon,
  ObjectiveSummary,
  ObjectiveMainInfo,
  ObjectiveContentWrapper,
  ObjectiveScoreDisplay,
  ObjectiveExpandedContent,
  ObjectiveAutoEvalButton,
  EvaluationFormContainer,
  FormTitle,
  RatingGrid,
  RatingOption,
  CommentSection,
  CommentLabel2,
  CommentTextarea,
  FormActions,
  SaveButton,
  CancelButton,
  SelfAssessmentDisplay,
  AssessmentHeader,
  AssessmentTitle,
  EditButton,
  AssessmentRating,
  RatingLabel,
  RatingBadge,
  AssessmentComment,
  SkillName,
  InfoIcon,
  SkillTitle,
  SkillWeight,
  SkillSummary,
  SectionCard,
  SectionHeader,
  SectionTitle2,
  EvaluationResultHeader,
  EvaluationResultIcon,
  EvaluationResultTitle,
  EvaluationResultLabel,
  EvaluationComment,
  EvaluationCommentLabel,
  EvaluationFormContainerManager,
  EvaluationResultsContainer,
  FinalEvaluationCard,
  FinalEvaluationLabel,
  FinalEvaluationValue,
} from "./myEvaluationDataModern.styled";
import { Tooltip } from "react-tooltip";
import { EvaluationCommentText } from "../evaluationManagerForm/evaluationManagerForm.styled";
import { NoEvaluationMessage } from "../evaluationForm/evaluationForm.modern.styled";
import { USER_PERMISSIONS } from "../../../security/permissions/rolesPermission";
import { Can } from "../../../security/permissions/Can";

const MyEvaluationModernView = (props) => {
  const { t } = useTranslation();
  const { data, onAutoEval, onSaveObjective, user } = props;
  let status;

  // Estados para colapsar/expandir
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [expandedSkills, setExpandedSkills] = useState({});

  // Estados para formularios de autoevaluación
  const [showObjectiveForm, setShowObjectiveForm] = useState({});
  const [showSkillForm, setShowSkillForm] = useState({});
  const [objectiveRatings, setObjectiveRatings] = useState({});
  const [objectiveComments, setObjectiveComments] = useState({});
  const [skillRatings, setSkillRatings] = useState({});
  const [skillComments, setSkillComments] = useState({});

  const toggleObjective = (index) => {
    setExpandedObjectives((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleSkill = (index) => {
    setExpandedSkills((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  useEffect(() => {
    if (data) {
      setObjectiveRatings({
        1: data.pointAuto01 || null,
        2: data.pointAuto02 || null,
        3: data.pointAuto03 || null,
        4: data.pointAuto04 || null,
        5: data.pointAuto05 || null,
      });

      const skills = {};
      let indexData = 0;
      for (const prof of data.professionalSkill) {
        skills[indexData] = prof.autoPoints || null;
        indexData++;
      }
      setSkillRatings(skills);
      if (data.comments && data.comments.length > 0) {
        const autoComments = {};
        const autoSkillComments = {};
        for (const comment of data.comments.filter((c) => c.type === "auto")) {
          autoComments[comment.obj] = comment.comment;
        }
        for (const comment of data.comments.filter(
          (c) => c.type === "autoSkill"
        )) {
          autoSkillComments[comment.obj] = comment.comment;
        }
        setSkillComments(autoSkillComments);
        setObjectiveComments(autoComments);
      }
    }
  }, [data]);

  // Funciones para manejar el formulario de autoevaluación de objetivos
  const handleShowObjectiveForm = (index) => {
    setShowObjectiveForm((prev) => ({ ...prev, [index]: true }));
  };

  const handleCancelObjectiveForm = (index) => {
    setShowObjectiveForm((prev) => ({ ...prev, [index]: false }));
  };

  const handleObjectiveRatingChange = (index, rating) => {
    setObjectiveRatings((prev) => ({ ...prev, [index]: rating }));
  };

  const handleObjectiveCommentChange = (index, comment) => {
    setObjectiveComments((prev) => ({ ...prev, [index]: comment }));
  };

  const handleSaveObjectiveAssessment = (index) => {
    // Aquí se simulará el envío - implementación futura
    setShowObjectiveForm((prev) => ({ ...prev, [index]: false }));
    onSaveObjective({
      objectiveIndex: index,
      rating: objectiveRatings[index],
      comment: objectiveComments[index],
      type: "obj",
    });
  };

  const handleSaveSkillAssessment = (index) => {
    // Aquí se simulará el envío - implementación futura

    // console.log("Saving skill assessment:", {
    //   skillIndex: index,
    //   rating: skillRatings[index],
    //   comment: skillComments[index],
    // });
    setShowSkillForm((prev) => ({ ...prev, [index]: false }));

    onSaveObjective({
      objectiveIndex: index,
      rating: skillRatings[index],
      comment: skillComments[index],
      type: "skill",
    });
  };

  // Funciones para manejar el formulario de autoevaluación de competencias
  const handleShowSkillForm = (index) => {
    setShowSkillForm((prev) => ({ ...prev, [index]: true }));
  };

  const handleCancelSkillForm = (index) => {
    setShowSkillForm((prev) => ({ ...prev, [index]: false }));
  };

  const handleSkillRatingChange = (index, rating) => {
    setSkillRatings((prev) => ({ ...prev, [index]: rating }));
  };

  const handleSkillCommentChange = (index, comment) => {
    setSkillComments((prev) => ({ ...prev, [index]: comment }));
  };

  // Opciones de calificación
  const ratingOptions = [
    { code: 5, label: t("rating.A") },
    { code: 4, label: t("rating.BA") },
    { code: 3, label: t("rating.B") },
    { code: 2, label: t("rating.BC") },
    { code: 1, label: t("rating.C") },
    { code: -1, label: t("rating.-") },
  ];

  // Función para obtener los colores según el código de rating
  const getRatingColors = (code) => {
    switch (code) {
      case 5:
        return {
          background: "#10b981", // Verde brillante - Excelente
          border: "#059669",
          text: "#ffffff",
        };
      case 4:
        return {
          background: "#84cc16", // Verde lima - Muy bueno
          border: "#65a30d",
          text: "#ffffff",
        };
      case 3:
        return {
          background: "#f9ae17", // Amarillo corporativo - Cumple
          border: "#d97706",
          text: "#ffffff",
        };
      case 2:
        return {
          background: "#f97316", // Naranja - Por debajo
          border: "#ea580c",
          text: "#ffffff",
        };
      case 1:
        return {
          background: "#ef4444", // Rojo - Muy por debajo
          border: "#dc2626",
          text: "#ffffff",
        };
      case -1:
        return {
          background: "#9ca3af", // Gris neutro - No aplica
          border: "#6b7280",
          text: "#ffffff",
        };
      default:
        return {
          background: "#ffffff",
          border: "#e5e7eb",
          text: "#1f2937",
        };
    }
  };

  // Validar si se puede guardar un objetivo
  const canSaveObjective = (index) => {
    const rating = objectiveRatings[index];
    if (!rating && rating !== -1) return false;
    if (rating === -1) {
      const comment = objectiveComments[index];
      return comment && comment.trim().length > 0;
    }
    return true;
  };

  // Validar si se puede guardar una competencia
  const canSaveSkill = (index) => {
    const rating = skillRatings[index];
    if (!rating && rating !== -1) return false;
    if (rating === -1) {
      const comment = skillComments[index];
      return comment && comment.trim().length > 0;
    }
    return true;
  };

  if (data) {
    status = EVAL_STATUS.find((f) => f.code === data.status);
  }

  const comments = {};

  if (data && data.comments && data.comments.length > 0) {
    for (const comment of data.comments) {
      comments[`${comment.type}${comment.obj}`] = comment.comment;
    }
  }

  const getPoints = (point) => {
    let label;
    if (point !== undefined) {
      const info = POINTS_ALL.find((p) => p.code === point);
      if (info) {
        label = t(info.label);
      }
    } else {
      label = t("point.na");
    }
    return label;
  };

  const getPointsValue = (point) => {
    if (point !== undefined) {
      const info = POINTS_ALL.find((p) => p.code === point);
      return info ? info.code : 0;
    }
    return 0;
  };

  const calculateProgress = (points, weight) => {
    const maxPoints = 5;
    const pointValue = getPointsValue(points);
    return (pointValue / maxPoints) * 100;
  };

  const calculateTotalScore = () => {
    if (!data) return 0;

    let totalScore = 0;
    let totalWeight = 0;

    // Objectives
    const objectives = [
      { points: data.point01, weight: data.weightCriterial01 },
      { points: data.point02, weight: data.weightCriterial02 },
      { points: data.point03, weight: data.weightCriterial03 },
      { points: data.point04, weight: data.weightCriterial04 },
      { points: data.point05, weight: data.weightCriterial05 },
    ];

    objectives.forEach((obj) => {
      if (obj.points !== undefined && obj.weight) {
        const pointValue = getPointsValue(obj.points);
        totalScore += (pointValue * obj.weight) / 5;
        totalWeight += obj.weight;
      }
    });

    // Skills
    if (data.professionalSkill) {
      data.professionalSkill.forEach((skill) => {
        if (skill.points !== undefined && skill.weight) {
          const pointValue = getPointsValue(skill.points);
          totalScore += (pointValue * skill.weight) / 5;
          totalWeight += skill.weight;
        }
      });
    }

    return totalWeight > 0 ? Math.round((totalScore / totalWeight) * 100) : 0;
  };

  const renderComment = (id, isManager = false) => {
    if (!comments[id]) return null;

    return (
      <CommentBox isManager={isManager}>
        <CommentLabel>
          {isManager
            ? t("forms.evaluation.labels.managerComment")
            : t("forms.evaluation.labels.employeeComment")}
          :
        </CommentLabel>
        <CommentText>{comments[id]}</CommentText>
      </CommentBox>
    );
  };

  const renderObjective = (
    index,
    criterial,
    weight,
    autoPoint,
    managerPoint
  ) => {
    if (!criterial) return null;

    const isExpanded = expandedObjectives[index];
    const showTruncated = criterial.length > 0;

    const hasManagerEval = data?.[`point0${index}`];

    return (
      <ObjectiveCard key={`objective_${index}`}>
        <ObjectiveHeader onClick={() => toggleObjective(index)}>
          <ObjectiveMainInfo>
            <ObjectiveNumber>{index}</ObjectiveNumber>
            <ObjectiveContentWrapper>
              <ObjectiveSummary>
                <ObjectiveTitle>
                  {t("forms.evaluation.labels.objective")} #{index}
                  <ObjectiveWeight>
                    {t("forms.evaluation.labels.weight")}: {weight}%
                  </ObjectiveWeight>
                  <CompletionIndicator
                    $completed={autoPoint !== undefined && autoPoint !== 0}
                  >
                    <i
                      className={`fa-solid ${
                        autoPoint !== undefined && autoPoint !== 0
                          ? "fa-check-circle"
                          : "fa-circle"
                      }`}
                    />
                    {autoPoint !== undefined && autoPoint !== 0
                      ? t("forms.evaluation.labels.completed")
                      : t("forms.evaluation.labels.pending")}
                  </CompletionIndicator>
                </ObjectiveTitle>
              </ObjectiveSummary>
              {!isExpanded && showTruncated && (
                <ObjectiveDescription>
                  {truncateText(criterial, 200)}
                </ObjectiveDescription>
              )}
              {isExpanded && (
                <ObjectiveDescription>{criterial}</ObjectiveDescription>
              )}
            </ObjectiveContentWrapper>
          </ObjectiveMainInfo>

          <ObjectiveScoreDisplay>
            {showTruncated && (
              <CollapseIcon
                className={`fa-solid fa-chevron-down ${
                  isExpanded ? "expanded" : ""
                }`}
              />
            )}
          </ObjectiveScoreDisplay>
        </ObjectiveHeader>

        {isExpanded && (
          <>
            <EvaluationResultsContainer>
              <ObjectiveExpandedContent>
                {/* <ProgressBar>
              <ProgressFill progress={calculateProgress(autoPoint, weight)} />
            </ProgressBar> */}

                {/* Mostrar autoevaluación guardada o formulario */}
                {objectiveRatings[index] && !showObjectiveForm[index] ? (
                  <SelfAssessmentDisplay>
                    <AssessmentHeader>
                      <AssessmentTitle>
                        {t("forms.evaluation.labels.yourSelfAssessment")}
                      </AssessmentTitle>
                      {data?.autoEvalAllowed && (
                        <EditButton
                          onClick={() => handleShowObjectiveForm(index)}
                        >
                          <i className="fa-solid fa-pen" />
                          {t("forms.evaluation.buttons.edit")}
                        </EditButton>
                      )}
                    </AssessmentHeader>
                    <AssessmentRating>
                      <RatingLabel>
                        {t("forms.evaluation.labels.rating")}:
                      </RatingLabel>
                      <RatingBadge>
                        {
                          ratingOptions.find(
                            (r) => r.code === objectiveRatings[index]
                          )?.label
                        }
                      </RatingBadge>
                    </AssessmentRating>
                    {objectiveComments[index] && (
                      <AssessmentComment>
                        {objectiveComments[index]}
                      </AssessmentComment>
                    )}
                  </SelfAssessmentDisplay>
                ) : showObjectiveForm[index] && data?.autoEvalAllowed ? (
                  <EvaluationFormContainer>
                    <FormTitle>
                      {t("forms.evaluation.labels.ratePerformance")}
                    </FormTitle>

                    <RatingGrid>
                      {ratingOptions.map((option) => {
                        const colors = getRatingColors(option.code);
                        return (
                          <RatingOption
                            key={option.code}
                            $selected={objectiveRatings[index] === option.code}
                            $bgColor={colors.background}
                            $borderColor={colors.border}
                            $textColor={colors.text}
                            onClick={() =>
                              handleObjectiveRatingChange(index, option.code)
                            }
                          >
                            {option.label}
                          </RatingOption>
                        );
                      })}
                    </RatingGrid>
                    {/* {objectiveComments[index] && ( */}
                      <CommentSection>
                        <CommentLabel2>
                          {t("forms.evaluation.labels.personalComment")}
                          {objectiveRatings[index] === -1 ? (
                            <span
                              style={{ color: "#E03A85", marginLeft: "4px" }}
                            >
                              *
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginLeft: "8px",
                                fontWeight: "400",
                              }}
                            >
                              ({t("forms.evaluation.labels.optional")})
                            </span>
                          )}
                        </CommentLabel2>
                        <CommentTextarea
                          placeholder={t(
                            "forms.evaluation.labels.commentPlaceholder"
                          )}
                          value={objectiveComments[index] || ""}
                          onChange={(e) =>
                            handleObjectiveCommentChange(index, e.target.value)
                          }
                        />
                      </CommentSection>
                    {/* // )} */}

                    <FormActions>
                      <SaveButton
                        className="btn-primary"
                        onClick={() => handleSaveObjectiveAssessment(index)}
                        disabled={!canSaveObjective(index)}
                      >
                        <i className="fa-solid fa-check-circle" />
                        {t("forms.evaluation.buttons.saveAssessment")}
                      </SaveButton>
                      <CancelButton
                        onClick={() => handleCancelObjectiveForm(index)}
                      >
                        <i className="fa-solid fa-times" />
                        {t("forms.evaluation.buttons.cancel")}
                      </CancelButton>
                    </FormActions>
                  </EvaluationFormContainer>
                ) : data?.autoEvalAllowed ? (
                  <ObjectiveAutoEvalButton
                    onClick={() => handleShowObjectiveForm(index)}
                  >
                    <i className="fa-solid fa-pen-to-square" />
                    {t("forms.evaluation.buttons.autoEval")}
                  </ObjectiveAutoEvalButton>
                ) : null}
              </ObjectiveExpandedContent>

              {hasManagerEval && data.status === EVAL_STATUS_NOTIFIED.code && (
                <EvaluationFormContainerManager $type="manager">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="manager">
                      <i className="fa-solid fa-user-tie"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="manager">
                      {t("forms.evaluation.labels.managerEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="eval">
                      {t("forms.evaluation.labels.managerScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="eval">
                        {getPoints(data[`point0${index}`])}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {/* <EvaluationResultScore $type="manager">
                        {getPoints(evaluation[`point0${index}`])}
                      </EvaluationResultScore> */}
                  {/* <EvaluationResultLabel $type="manager">
                        {t("forms.evaluation.labels.managerScore")}
                      </EvaluationResultLabel> */}
                  {comments[`final${index}`] && (
                    <EvaluationComment $type="manager">
                      <EvaluationCommentLabel $type="manager">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.managerComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="manager">
                        {comments[`final${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationFormContainerManager>
                // ) : (
                //   <NoEvaluationMessage>
                //     <i className="fa-solid fa-hourglass-half"></i>
                //     {t("forms.evaluation.labels.pendingManagerEvaluation")}
                //   </NoEvaluationMessage>
              )}
            </EvaluationResultsContainer>
          </>
        )}
      </ObjectiveCard>
    );
  };

  const renderCompetency = (skill, index) => {
    const isExpanded = expandedSkills[index];
    const description = t(`skill:skill_${index + 1}.description`);
    const showTruncated = description.length > 0;

    return (
      <CompetencyCard key={`skill_${index}`} className="mb-3">
        <CompetencyHeader onClick={() => toggleSkill(index)}>
          <div className="row align-items-center">
            <div className="col-auto">
              <ObjectiveNumber>{index + 1}</ObjectiveNumber>
            </div>
            <div className="col">
              <SkillWeight>
                {t("forms.evaluation.labels.weight")}: {skill.weight}%
              </SkillWeight>
              <CompletionIndicator
                $completed={
                  skill.autoPoints !== undefined && skill.autoPoints !== 0
                }
              >
                <i
                  className={`fa-solid ${
                    skill.autoPoints !== undefined && skill.autoPoints !== 0
                      ? "fa-check-circle"
                      : "fa-circle"
                  }`}
                />
                {skill.autoPoints !== undefined && skill.autoPoints !== 0
                  ? t("forms.evaluation.labels.completed")
                  : t("forms.evaluation.labels.pending")}
              </CompletionIndicator>

              <SkillSummary className="mt-2">
                <SkillTitle>{skill.name}</SkillTitle>
                <SkillName>
                  <InfoIcon
                    className="fas fa-info-circle"
                    data-tooltip-id={`tooltip-skill-${index}`}
                  />
                </SkillName>
                <Tooltip id={`tooltip-skill-${index}`}>
                  <div style={{ maxWidth: "300px" }}>{description}</div>
                </Tooltip>

                {/* <ObjectiveDescription>
                  {isExpanded || !showTruncated
                    ? description
                    : truncateText(description, 200)}
                </ObjectiveDescription> */}
              </SkillSummary>
            </div>
            {showTruncated && (
              <div className="col-auto">
                <CollapseIcon
                  className={`fa-solid fa-chevron-down ${
                    isExpanded ? "expanded" : ""
                  }`}
                />
              </div>
            )}
          </div>
        </CompetencyHeader>

        {isExpanded && (
          <CompetencyBody>
            {/* Mostrar autoevaluación guardada o formulario */}
            {skillRatings[index] && !showSkillForm[index] ? (
              <SelfAssessmentDisplay>
                <AssessmentHeader>
                  <AssessmentTitle>
                    {t("forms.evaluation.labels.yourSelfAssessment")}
                  </AssessmentTitle>
                  {data?.autoEvalAllowed && (
                    <EditButton onClick={() => handleShowSkillForm(index)}>
                      <i className="fa-solid fa-pen" />
                      {t("forms.evaluation.buttons.edit")}
                    </EditButton>
                  )}
                </AssessmentHeader>
                <AssessmentRating>
                  <RatingLabel>
                    {t("forms.evaluation.labels.rating")}:
                  </RatingLabel>
                  <RatingBadge>
                    {
                      ratingOptions.find((r) => r.code === skillRatings[index])
                        ?.label
                    }
                  </RatingBadge>
                </AssessmentRating>
                {skillComments[index] && (
                  <AssessmentComment>{skillComments[index]}</AssessmentComment>
                )}
              </SelfAssessmentDisplay>
            ) : showSkillForm[index] && data?.autoEvalAllowed ? (
              <EvaluationFormContainer>
                <FormTitle>
                  {t("forms.evaluation.labels.ratePerformance")}
                </FormTitle>

                <RatingGrid>
                  {ratingOptions.map((option) => {
                    const colors = getRatingColors(option.code);
                    return (
                      <RatingOption
                        key={option.code}
                        $selected={skillRatings[index] === option.code}
                        $bgColor={colors.background}
                        $borderColor={colors.border}
                        $textColor={colors.text}
                        onClick={() =>
                          handleSkillRatingChange(index, option.code)
                        }
                      >
                        {option.label}
                      </RatingOption>
                    );
                  })}
                </RatingGrid>

                <CommentSection>
                  <CommentLabel2>
                    {t("forms.evaluation.labels.personalComment")}
                    {skillRatings[index] === -1 ? (
                      <span style={{ color: "#E03A85", marginLeft: "4px" }}>
                        *
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          marginLeft: "8px",
                          fontWeight: "400",
                        }}
                      >
                        ({t("forms.evaluation.labels.optional")})
                      </span>
                    )}
                  </CommentLabel2>
                  <CommentTextarea
                    placeholder={t(
                      "forms.evaluation.labels.commentPlaceholder"
                    )}
                    value={skillComments[index] || ""}
                    onChange={(e) =>
                      handleSkillCommentChange(index, e.target.value)
                    }
                  />
                </CommentSection>

                <FormActions>
                  <SaveButton
                    className="btn-primary"
                    onClick={() => handleSaveSkillAssessment(index)}
                    disabled={!canSaveSkill(index)}
                  >
                    <i className="fa-solid fa-check-circle" />
                    {t("forms.evaluation.buttons.saveAssessment")}
                  </SaveButton>
                  <CancelButton onClick={() => handleCancelSkillForm(index)}>
                    <i className="fa-solid fa-times" />
                    {t("forms.evaluation.buttons.cancel")}
                  </CancelButton>
                </FormActions>
              </EvaluationFormContainer>
            ) : data?.autoEvalAllowed ? (
              <ObjectiveAutoEvalButton
                onClick={() => handleShowSkillForm(index)}
              >
                <i className="fa-solid fa-pen-to-square" />
                {t("forms.evaluation.buttons.autoEval")}
              </ObjectiveAutoEvalButton>
            ) : null}
          </CompetencyBody>
        )}
      </CompetencyCard>
    );
  };

  const totalScore = calculateTotalScore();

  // Función para verificar si todos los objetivos y competencias están autoevaluados
  const isAllEvaluated = () => {
    if (!data) return false;

    // Verificar objetivos (1-5)
    const objectives = [
      { criterial: data.criterial01, rating: data.pointAuto01 },
      { criterial: data.criterial02, rating: data.pointAuto02 },
      { criterial: data.criterial03, rating: data.pointAuto03 },
      { criterial: data.criterial04, rating: data.pointAuto04 },
      { criterial: data.criterial05, rating: data.pointAuto05 },
    ];

    // Verificar que todos los objetivos existentes estén evaluados
    for (const obj of objectives) {
      if (obj.criterial) {
        // Si el rating es null o undefined, no está evaluado
        if (obj.rating === null || obj.rating === undefined) {
          return false;
        }
        // Si es -1, verificar que tenga comentario
        if (obj.rating === -1) {
          const index = objectives.indexOf(obj) + 1;
          const comment = objectiveComments[index];
          if (!comment || comment.trim().length === 0) {
            return false;
          }
        }
      }
    }

    // Verificar competencias
    if (data.professionalSkill && data.professionalSkill.length > 0) {
      for (let i = 0; i < data.professionalSkill.length; i++) {
        // Si el rating es null o undefined, no está evaluado
        if (
          data.professionalSkill[i].autoPoints === null ||
          data.professionalSkill[i].autoPoints === undefined
        ) {
          return false;
        }
        // Si es -1, verificar que tenga comentario
        if (data.professionalSkill[i].autoPoints === -1) {
          const comment = skillComments[i];
          if (!comment || comment.trim().length === 0) {
            return false;
          }
        }
      }
    }

    return true;
  };

  const allEvaluated = isAllEvaluated();

  return (
    <>
      {/* <div className="row justify-content-center mt-5">
        <div className="col-12 col-md-11">
          <div className="row align-items-start">
            <div className="col-12 col-lg-12 mb-3">
              <h2 className="title">
                {t("forms.evaluation.detail")} {data?.year}
              </h2>
              <StatusBadge status={data?.status}>
                {t(status?.label)}
              </StatusBadge>
            </div>
          </div>
        </div>
      </div> */}

      <div className="row justify-content-center mt-5">
        <div className="col-12 col-md-11">
          <InfoCard>
            <UserAvatar>
              {data?.dataUser?.name?.charAt(0)}
              {data?.dataUser?.surname?.charAt(0)}
            </UserAvatar>
            <UserInfo>
              <UserTitle>
                {`${data?.dataUser?.name} ${data?.dataUser?.surname}`}
              </UserTitle>
              <UserName>
                {t(status?.label)}
                <span style={{ marginLeft: "4px" }}>•</span>
                <YearBadge>Evaluación {data?.year}</YearBadge>
              </UserName>
            </UserInfo>
            {/* {data?.finalPoints && data?.finalPoints !== "-" && (
              <FinalEvaluationCard>
                
                <FinalEvaluationValue>
                  {data.finalPoints} ({t(`rating.${data.finalPoints}`)})
                </FinalEvaluationValue>
              </FinalEvaluationCard>
            )} */}
          </InfoCard>
        </div>
      </div>

      {/* 
        // <Can do={USER_PERMISSIONS.VIEW_FINAL_POINT}>
        // <div className="row justify-content-center ">
        //   <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
        //     <FinalEvaluationCard>
        //       <FinalEvaluationLabel>
        //         {t("forms.evaluation.labels.finalEvaluation")}
        //       </FinalEvaluationLabel>
        //       <FinalEvaluationValue>
        //         {data.finalPoints} ({t(`rating.${data.finalPoints}`)})
        //       </FinalEvaluationValue>
        //     </FinalEvaluationCard>
        //   </div>
        // </div>
        // </Can>
       */}
      {/* 
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionTitle>
            <i
              className="fa-solid fa-bullseye"
              style={{ marginRight: "10px" }}
            />
            {t("forms.evaluation.labels.criterialObj")}
          </SectionTitle>
        </div>
      </div> */}
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionCard>
            <SectionHeader>
              <SectionTitle2>
                <i
                  className="fa-solid fa-bullseye"
                  style={{ marginRight: "10px" }}
                />
                {t("forms.evaluation.labels.criterialObj")}
              </SectionTitle2>
            </SectionHeader>

            <div className="row">
              <div className="col-12">
                {renderObjective(
                  1,
                  data?.criterial01,
                  data?.weightCriterial01,
                  data?.pointAuto01,
                  data?.point01
                )}
                {renderObjective(
                  2,
                  data?.criterial02,
                  data?.weightCriterial02,
                  data?.pointAuto02,
                  data?.point02
                )}
                {renderObjective(
                  3,
                  data?.criterial03,
                  data?.weightCriterial03,
                  data?.pointAuto03,
                  data?.point03
                )}
                {renderObjective(
                  4,
                  data?.criterial04,
                  data?.weightCriterial04,
                  data?.pointAuto04,
                  data?.point04
                )}
                {renderObjective(
                  5,
                  data?.criterial05,
                  data?.weightCriterial05,
                  data?.pointAuto05,
                  data?.point05
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionTitle>
            <i className="fa-solid fa-star" style={{ marginRight: "10px" }} />
            {t("forms.evaluation.labels.criterialStd")}
          </SectionTitle>
        </div>
      </div> */}
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionCard>
            <SectionHeader>
              <SectionTitle2>
                <i className="fas fa-award" style={{ marginRight: "10px" }} />
                {t("forms.evaluation.labels.criterialStd")}
              </SectionTitle2>
            </SectionHeader>

            <div className="row">
              <div className="col-12">
                {data?.professionalSkill?.map((skill, index) =>
                  renderCompetency(skill, index)
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
      {data?.autoEvalAllowed && (
        <div className="row justify-content-center mt-5 mb-5">
          <div className="col-12 col-md-11 text-center">
            <ActionButton onClick={() => onAutoEval()} disabled={!allEvaluated}>
              <i
                className="fa-solid fa-pen-to-square"
                style={{ marginRight: "8px" }}
              />
              {t("forms.evaluation.buttons.autoEval")}
            </ActionButton>
          </div>
        </div>
      )}
      {/* 
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <div className="mb-5"></div>
        </div>
      </div> */}
    </>
  );
};

MyEvaluationModernView.propTypes = {};

export default MyEvaluationModernView;
